import React, { useState, useEffect } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import InputBox from "./InputBox";
import "./InputBox.scss";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { characterLimitationOfTextArea } from "@/Constant";
import { MotifErrorMessage, MotifLabel } from "@ey-xd/motif-react";

type ControlInputBoxType<T extends FieldValues> = {
  required?: boolean;
  isTextArea?: boolean;
  control: Control<T>;
  name: Path<T>;
  showLabel: boolean;
  label: string;
  readonly?: boolean;
  defaultValue?: string;
  setValue?: any;
  charLimit?: number;
  setError?: any;
};

export default function ControlEmailTextBox<T extends FieldValues>({
  name,
  control,
  required = false,
  isTextArea = true,
  showLabel,
  label,
  readonly = false,
  charLimit = characterLimitationOfTextArea,
}: ControlInputBoxType<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: required,
      validate: (value: any) => {
        let flag = true;
        let exp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9._%+-]*ey\.com$/;
        let emailsArray = value?.split(";");
        if (emailsArray?.[emailsArray.length - 1] === "") {
          emailsArray.pop(); // Remove the last empty element if it exists
        }
        if (emailsArray?.length > 5) {
          return "pages.caseSetup.caseDetails.emailValidationCount";
        } else if (value?.includes(",")) {
          return "pages.caseSetup.caseDetails.emailSeperationValidation";
        } else {
          emailsArray?.forEach((email: string) => {
            if (!exp.test(email?.trim())) {
              flag = false;
            }
          });

          return flag ? true : "pages.caseSetup.caseDetails.emailValidation";
        }
      },
    },
  });
  const { t } = useTranslation();

  const [currentChar, setCurrentChar] = useState(0);

  useEffect(() => {
    setCurrentChar(field.value?.length || 0);
  }, [field.value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const charCount = e.target.value.length;
    if (charCount > charLimit) {
      e.target.value = e.target.value.slice(0, charLimit);
      setCurrentChar(charLimit);
    } else {
      setCurrentChar(charCount);
    }
    field.onChange(e);
  };

  return (
    <>
      {showLabel && (
        <div className="formFieldLabel">
          <label htmlFor="input" aria-label={label}>
            {t(label)}
            {required ? <span className="requiredSymbol">*</span> : ""}
          </label>
        </div>
      )}
      <Controller
        name={field.name}
        control={control}
        rules={{ required }}
        render={({ field: { onChange, value = field.value, ...args } }) => {
          return (
            <>
              <div>
                <InputBox
                  id={name as string}
                  required={true}
                  value={
                    typeof value === "string" || typeof value === "number"
                      ? value
                      : ""
                  }
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => {
                    if (/^[^ ,]+$/.test(e.target.value) || e.target.value === "") {
                      handleChange(e);
                      onChange(e);
                    }
                  }}
                  isTextArea={isTextArea}
                  readonly={readonly}
                  maxLength={charLimit.toString()}
                  {...args}
                />
                {isTextArea && (
                  <div className="text-right">{`${currentChar}/${charLimit}`}</div>
                )}
              </div>
              <MotifLabel>Note: Seperate emails using semicolon.</MotifLabel>
              <MotifErrorMessage>
                {error?.type === "required"
                  ? t("pages.caseSetup.caseDetails.emailValidation")
                  : error?.type === "validate"
                  ? t(
                      error.message ||
                        "pages.caseSetup.caseDetails.emailValidation"
                    )
                  : ""}
              </MotifErrorMessage>
            </>
          );
        }}
      />
    </>
  );
}
